// src/pages/ManagePage.js
//
// Manage visits page (backoffice / internal operations).
//
// UX goals
// --------
// 1) Collapsible filters drawer persisted in localStorage.
// 2) Stable table with:
//      - sticky header
//      - resizable columns persisted in localStorage
//      - double-click auto-fit per column
//      - single scroll owner (.mp-table-scroller)
// 3) Shared pagination footer rendered inside the local table wrapper.
// 4) Row edits commit on blur/change without refetch; optimistic local update.
//
// IMPORTANT PAGINATION UPDATE
// ---------------------------
// Pagination is now handled through shared building blocks:
//
//   src/hooks/useClientPagination.js
//   src/components/common/PaginationFooter.jsx
//   src/components/common/PaginationFooter.css
//
// This page should not implement a fixed footer anymore.
//
// Why:
// - the sidebar can now change width,
// - a fixed footer would need to know the current app-shell geometry,
// - inline pagination naturally follows the content area,
// - this keeps pagination reusable for visits, products, services, reports, etc.
//
// ManagePage still owns:
// - local data fetching,
// - local table rendering,
// - local column resizing,
// - local visual skin for pagination via .mp-pagination.
//
// IMPORTANT LOADING UPDATE
// ------------------------
// The current table stays mounted during fetch.
// Old rows remain visible until the new payload arrives.
// A lightweight loading overlay is shown on top of the table area.
//
// Why this is better:
// - layout remains stable,
// - sticky header does not jump,
// - pagination footer does not need JS clearance measurement,
// - users keep visual context while refresh is in flight.

import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

import PendingStockOperationNotice from "../components/stock/PendingStockOperationNotice";
import StockPostingContextCard from "../components/stock/StockPostingContextCard";
import StockMovementAuditLoader from "../components/stock/StockMovementAuditLoader";
import { stockPostingErrorMessage } from "../utils/stockPostingPresentation";
import AuthContext from "../context/AuthContext";
import {
  fetchVisits,
  updateVisitField,
  deleteVisit,
} from "../services/visitService";
import { checkVisit, uncheckVisit } from "../services/visitCheckService";
import { fetchUsers } from "../services/userService";

import useEvaluationPeriod from "../hooks/useEvaluationPeriod";
import useClientPagination from "../hooks/useClientPagination";

import EvaluationPeriodPicker from "../components/filters/EvaluationPeriodPicker";
import PaginationFooter from "../components/common/PaginationFooter";
import VisitCheckWarningToast from "../components/visits/VisitCheckWarningToast";

import { EVALUATION_PERIOD_TYPES } from "../utils/evaluationPeriodUtils";

import "./ManagePage.css";

/* =============================================================================
   LocalStorage keys
   ============================================================================= */

const WIDTHS_STORAGE_KEY = "mp_col_widths";
const LAST_QUERY_KEY = "mp_last_query";
const FILTERS_OPEN_KEY = "mp_filters_open";
const PAGE_SIZE_KEY = "mp_page_size";

/* =============================================================================
   Table model and sizing constants
   ============================================================================= */

const COL_KEYS = [
  "id",
  "date",
  "reservation",
  "category",
  "service",
  "client",
  "duration",
  "value3p",
  "valueHB",
  "emp3p",
  "employeeId",
  "status3p",
  "statusHB",
  "comment",
  "check",
  "actions",
];

const MIN_COL_W = 24;
const MAX_AUTOFIT_W = 520;

/* =============================================================================
   Utilities: storage helpers
   ============================================================================= */

function loadJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore quota/private-mode errors.
  }
}

function readPositiveNumberFromStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  } catch {
    return fallback;
  }
}

/* =============================================================================
   Utilities: auto-fit measurement
   ============================================================================= */

function getCanvasFontFromElement(el) {
  const cs = window.getComputedStyle(el);
  const fontStyle = cs.fontStyle || "normal";
  const fontVariant = cs.fontVariant || "normal";
  const fontWeight = cs.fontWeight || "400";
  const fontSize = cs.fontSize || "12px";
  const fontFamily =
    cs.fontFamily || 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  return `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize} ${fontFamily}`;
}

function getCellText(key, row) {
  switch (key) {
    case "id":
      return String(row.visit_id ?? "");
    case "date":
      return String(row.date_time ?? "");
    case "reservation":
      return String(row.reservation_id ?? "");
    case "category":
      return String(row.category ?? "");
    case "service":
      return String(row.service ?? "");
    case "client":
      return String(row.client ?? "");
    case "duration":
      return String(row.duration ?? "");
    case "value3p":
      return row.service_value3P != null ? String(row.service_value3P) : "";
    case "valueHB":
      return row.service_value != null ? String(row.service_value) : "";
    case "emp3p":
      return String(row.employee3P ?? "");
    case "employeeId":
      return String(row.employee_id ?? "");
    case "status3p":
      return String(row.status3P ?? "");
    case "statusHB":
      return String(row.status ?? "");
    case "comment":
      return String(row.status_comment ?? "");
    case "check":
      return row.checked ? "✓" : "";
    case "actions":
      return "Delete";
    default:
      return "";
  }
}

function estimateColumnWidth(key, headerLabel, rows, tableEl) {
  const font = tableEl
    ? getCanvasFontFromElement(tableEl)
    : 'normal normal 400 12px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.font = font;

  let max = ctx.measureText(headerLabel || "").width;

  for (const r of rows) {
    const w = ctx.measureText(getCellText(key, r)).width;
    if (w > max) max = w;
  }

  let width = Math.ceil(max + 16 + 6 + 10);

  const editorMin = {
    valueHB: 90,
    employeeId: 160,
    statusHB: 110,
    comment: 160,
    actions: 70,
    check: 40,
  };

  if (editorMin[key]) {
    width = Math.max(width, editorMin[key]);
  }

  return Math.max(MIN_COL_W, Math.min(width, MAX_AUTOFIT_W));
}

/* =============================================================================
   Utilities: normalize outbound values
   ============================================================================= */

function normalizeForField(field, raw) {
  if (field === "employee_id") {
    if (raw === "" || raw === undefined || raw === null) return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }

  if (field === "checked") {
    return !!raw;
  }

  if (field === "service_value") {
    if (raw === "" || raw === null || raw === undefined) return 0;
    const n = Number(raw);
    return Number.isNaN(n) ? 0 : n;
  }

  return raw;
}

/* =============================================================================
   Component
   ============================================================================= */

const ManagePage = () => {
  const { t } = useTranslation(["common", "visits", "stock"]);
  const { authState } = useContext(AuthContext);

  const currentUser = authState?.user || {};
  const role = currentUser?.role || "user";
  const isPrivileged = role === "superuser" || role === "reception";

  const forcedUserIdForUserRole =
    role === "user" && currentUser?.id != null ? String(currentUser.id) : null;

  const initialLastQuery = loadJSON(LAST_QUERY_KEY, null);

  /* ===========================================================================
     Shared evaluation-period controller
     =========================================================================== */

  const allowedEvaluationModes = useMemo(() => {
    if (role === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [role]);

  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  /* ===========================================================================
     Filters
     =========================================================================== */

  const [filters, setFilters] = useState(() => {
    const storedUserIds = Array.isArray(initialLastQuery?.userIds)
      ? initialLastQuery.userIds.map((x) => String(x))
      : [];

    const userIds =
      forcedUserIdForUserRole != null ? [forcedUserIdForUserRole] : storedUserIds;

    return { userIds };
  });

  const [filtersOpen, setFiltersOpen] = useState(() => {
    const saved = localStorage.getItem(FILTERS_OPEN_KEY);
    return saved ? saved === "1" : true;
  });

  const toggleFiltersOpen = useCallback(() => {
    setFiltersOpen((v) => {
      const next = !v;
      localStorage.setItem(FILTERS_OPEN_KEY, next ? "1" : "0");
      return next;
    });
  }, []);

  /* ===========================================================================
     Data + messages
     =========================================================================== */

  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);

  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // Non-blocking material consumption warning returned by visit check.
  // This is intentionally separate from hard errors: the visit checkbox may
  // succeed while some materials still need manager review.
  const [visitCheckWarning, setVisitCheckWarning] = useState(null);
  const [sourceStockResult, setSourceStockResult] = useState(null);
  const [sourceStockError, setSourceStockError] = useState(null);
  const [auditMovementId, setAuditMovementId] = useState(null);

  const [lastQuery, setLastQuery] = useState(() => initialLastQuery);

  /* ===========================================================================
     Shared pagination
     =========================================================================== */

  const initialPageSize = useMemo(
    () => readPositiveNumberFromStorage(PAGE_SIZE_KEY, 50),
    []
  );

  const {
    page,
    pageSize,
    pageCount,
    totalRows,
    paginatedItems: paginated,
    setPageSize: setPaginationPageSize,
    goPrev,
    goNext,
    resetPage,
  } = useClientPagination(data, {
    initialPageSize,
  });

  const handlePageSizeChange = useCallback(
    (nextPageSize) => {
      const safeNext = Number(nextPageSize);

      const normalized =
        Number.isFinite(safeNext) && safeNext > 0 ? safeNext : 50;

      setPaginationPageSize(normalized);
      localStorage.setItem(PAGE_SIZE_KEY, String(normalized));
    },
    [setPaginationPageSize]
  );

  const resetFilters = useCallback(() => {
    setFilters({
      userIds: forcedUserIdForUserRole != null ? [forcedUserIdForUserRole] : [],
    });

    resetPage();
    setError("");
    setMessage("");
  }, [forcedUserIdForUserRole, resetPage]);

  /* ===========================================================================
     Column widths
     =========================================================================== */

  const [colWidths, setColWidths] = useState(() =>
    loadJSON(WIDTHS_STORAGE_KEY, {})
  );

  useEffect(() => {
    saveJSON(WIDTHS_STORAGE_KEY, colWidths);
  }, [colWidths]);

  const resetAllWidths = useCallback(() => {
    localStorage.removeItem(WIDTHS_STORAGE_KEY);
    setColWidths({});
  }, []);

  const widthFor = useCallback(
    (key) => {
      const v = colWidths[key];
      return typeof v === "number" && v > 0 ? `${v}px` : undefined;
    },
    [colWidths]
  );

  const widthAttrFor = useCallback(
    (key) => {
      const v = colWidths[key];
      return typeof v === "number" && v > 0 ? v : undefined;
    },
    [colWidths]
  );

  const cellWidthStyle = useCallback(
    (key) => {
      const px = widthFor(key);
      return px ? { width: px, maxWidth: px } : undefined;
    },
    [widthFor]
  );

  /* ===========================================================================
     Column resizing
     =========================================================================== */

  const tableWrapRef = useRef(null);
  const resizeInfoRef = useRef(null);

  const onResizeMove = useCallback((ev) => {
    const info = resizeInfoRef.current;
    if (!info) return;

    const delta = ev.clientX - info.startX;
    const nextWidth = Math.max(MIN_COL_W, info.startWidth + delta);

    setColWidths((w) => ({ ...w, [info.key]: nextWidth }));
  }, []);

  const endResize = useCallback(() => {
    resizeInfoRef.current = null;
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", endResize);
  }, [onResizeMove]);

  const startResize = useCallback(
    (e, key) => {
      e.preventDefault();
      e.stopPropagation();

      const th = e.currentTarget.closest("th");
      const rect = th.getBoundingClientRect();

      resizeInfoRef.current = {
        key,
        startX: e.clientX,
        startWidth: rect.width,
      };

      window.addEventListener("mousemove", onResizeMove);
      window.addEventListener("mouseup", endResize);
    },
    [onResizeMove, endResize]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onResizeMove);
      window.removeEventListener("mouseup", endResize);
    };
  }, [onResizeMove, endResize]);

  const autoFitColumn = useCallback(
    (key) => {
      const headerMap = {
        id: t("visits:columns.id"),
        date: t("visits:columns.date"),
        reservation: t("visits:columns.reservation"),
        category: t("visits:columns.category"),
        service: t("visits:columns.service"),
        client: t("visits:columns.client"),
        duration: t("visits:columns.duration"),
        value3p: t("visits:columns.value3p"),
        valueHB: t("visits:columns.valueHB"),
        emp3p: t("visits:columns.emp3p"),
        employeeId: t("visits:columns.employeeId"),
        status3p: t("visits:columns.status3p"),
        statusHB: t("visits:columns.statusHB"),
        comment: t("visits:columns.comment"),
        check: t("visits:columns.check"),
        actions: t("visits:columns.actions"),
      };

      const tableEl = tableWrapRef.current?.querySelector("table");
      const w = estimateColumnWidth(key, headerMap[key] || key, data, tableEl);

      setColWidths((prev) => ({ ...prev, [key]: w }));
    },
    [data, t]
  );

  /* ===========================================================================
     Load users
     =========================================================================== */

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const usersData = await fetchUsers();
        if (!alive) return;

        setUsers(Array.isArray(usersData) ? usersData : []);
      } catch {
        // Non-blocking. Page works without user list.
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  /* ===========================================================================
     Query runner
     =========================================================================== */

  const runQuery = useCallback(
    async (q, { saveAsLastQuery }) => {
      if (!q?.from || !q?.to) {
        setError(t("visits:managePage.invalidPeriod"));
        return;
      }

      setIsFetching(true);
      setError("");
      setMessage("");
      setVisitCheckWarning(null);

      try {
        const response = await fetchVisits(q.from, q.to, q.userIds || []);
        const rows = Array.isArray(response) ? response : [];

        setData(rows);
        resetPage();

        if (saveAsLastQuery) {
          setLastQuery(q);
          saveJSON(LAST_QUERY_KEY, q);
        }

        if (rows.length === 0) {
          setMessage(t("visits:managePage.noDataSelectedRange"));
        }
      } catch (err) {
        setError(err?.message || t("visits:managePage.fetchFailed"));
      } finally {
        setIsFetching(false);
      }
    },
    [resetPage, t]
  );

  const handleApplyFilters = useCallback(() => {
    const q = {
      from: selectedFrom,
      to: selectedTo,
      userIds:
        forcedUserIdForUserRole != null
          ? [forcedUserIdForUserRole]
          : (filters.userIds || []).map(String),
      displayLabel: evaluationPeriod.displayLabel || "",
    };

    runQuery(q, { saveAsLastQuery: true });
  }, [
    selectedFrom,
    selectedTo,
    forcedUserIdForUserRole,
    filters.userIds,
    evaluationPeriod.displayLabel,
    runQuery,
  ]);

  const handleRefresh = useCallback(() => {
    if (!lastQuery?.from || !lastQuery?.to) {
      setMessage(t("visits:managePage.pickPeriodFirst"));
      setFiltersOpen(true);
      return;
    }

    runQuery(
      {
        from: lastQuery.from,
        to: lastQuery.to,
        userIds: (lastQuery.userIds || []).map(String),
        displayLabel: lastQuery.displayLabel || "",
      },
      { saveAsLastQuery: false }
    );
  }, [lastQuery, runQuery, t]);

  /* ===========================================================================
     Row editing
     =========================================================================== */

  const [drafts, setDrafts] = useState({});
  const [pendingByRow, setPendingByRow] = useState({});

  const isRowPending = useCallback(
    (visitId) => !!pendingByRow[String(visitId)],
    [pendingByRow]
  );

  const setDraft = useCallback((visitId, field, value) => {
    const id = String(visitId);

    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  }, []);

  const clearDraftField = useCallback((visitId, field) => {
    const id = String(visitId);

    setDrafts((prev) => {
      if (!prev[id]) return prev;

      const nextRow = { ...prev[id] };
      delete nextRow[field];

      const next = { ...prev };

      if (Object.keys(nextRow).length === 0) {
        delete next[id];
      } else {
        next[id] = nextRow;
      }

      return next;
    });
  }, []);

  const rowById = useMemo(() => {
    const m = new Map();

    for (const r of data) {
      m.set(String(r.visit_id), r);
    }

    return m;
  }, [data]);

  const commitField = useCallback(
    async (visitId, field, rawValueFallback) => {
      const id = String(visitId);
      if (isRowPending(id)) return;

      const row = rowById.get(id);
      if (!row) return;

      const draftValue =
        drafts[id] && Object.prototype.hasOwnProperty.call(drafts[id], field)
          ? drafts[id][field]
          : rawValueFallback;

      const nextValue = normalizeForField(field, draftValue);

      const currentValueRaw = (() => {
        switch (field) {
          case "service_value":
            return row.service_value ?? 0;
          case "employee_id":
            return row.employee_id ?? null;
          case "status":
            return row.status ?? "";
          case "status_comment":
            return row.status_comment ?? "";
          case "checked":
            return !!row.checked;
          default:
            return row[field];
        }
      })();

      const currentValue = normalizeForField(field, currentValueRaw);

      if (String(currentValue) === String(nextValue)) {
        clearDraftField(id, field);
        return;
      }

      setPendingByRow((p) => ({ ...p, [id]: true }));
      setError("");
      setMessage("");
      setVisitCheckWarning(null);

      try {
        // checked is now a domain action, not a plain field update.
        // Backend may post service material stock movements or create
        // non-blocking review exceptions. Other fields still use the legacy
        // update endpoint.
        const res = field === "checked"
          ? nextValue
            ? await checkVisit(Number(visitId))
            : await uncheckVisit(Number(visitId))
          : await updateVisitField(Number(visitId), field, nextValue);

        const serverFields =
          res?.updatedFields && typeof res.updatedFields === "object"
            ? res.updatedFields
            : { [field]: nextValue };

        setData((prev) =>
          prev.map((r) =>
            String(r.visit_id) === id ? { ...r, ...serverFields } : r
          )
        );

        clearDraftField(id, field);

        if (field === "checked") {
          setSourceStockError(null); setSourceStockResult(res.stock_posting || null);
          setMessage(res?.message || t("visits:managePage.checkedUpdated"));
          if (res?.warning) {
            setVisitCheckWarning({
              warning: res.warning,
              summary: res.materialConsumption || null,
            });
          }
        } else {
          setMessage(t("visits:managePage.fieldUpdated", { field }));
        }
      } catch (err) {
        const message = field === "checked" ? stockPostingErrorMessage(err, t) : err?.message || t("visits:managePage.fieldUpdateFailed", { field });
        setError(message);
        if (field === "checked") setSourceStockError(message);
      } finally {
        setPendingByRow((p) => {
          const next = { ...p };
          delete next[id];
          return next;
        });
      }
    },
    [drafts, rowById, isRowPending, clearDraftField, t]
  );

  const handleDeleteRow = useCallback(
    async (visitId) => {
      const id = String(visitId);
      if (isRowPending(id)) return;

      if (!window.confirm(t("visits:managePage.deleteConfirm", { visitId }))) {
        return;
      }

      setPendingByRow((p) => ({ ...p, [id]: true }));
      setError("");
      setMessage("");

      try {
        await deleteVisit(Number(visitId));
        setData((prev) => prev.filter((r) => String(r.visit_id) !== id));
        setMessage(t("visits:managePage.deleteSuccess", { visitId }));
      } catch (err) {
        setError(err?.message || t("visits:managePage.deleteFailed", { visitId }));
      } finally {
        setPendingByRow((p) => {
          const next = { ...p };
          delete next[id];
          return next;
        });
      }
    },
    [isRowPending, t]
  );

  /* ===========================================================================
     Auto-hide messages
     =========================================================================== */

  useEffect(() => {
    if (!error && !message && !visitCheckWarning) return;

    const t = setTimeout(() => {
      setError("");
      setMessage("");
      setVisitCheckWarning(null);
    }, visitCheckWarning ? 7000 : 3000);

    return () => clearTimeout(t);
  }, [error, message, visitCheckWarning]);

  /* ===========================================================================
     Render model
     =========================================================================== */

  const headerCols = useMemo(
    () => [
      { key: "id", label: t("visits:columns.id") },
      { key: "date", label: t("visits:columns.date") },
      { key: "reservation", label: t("visits:columns.reservation") },
      { key: "category", label: t("visits:columns.category") },
      { key: "service", label: t("visits:columns.service") },
      { key: "client", label: t("visits:columns.client") },
      { key: "duration", label: t("visits:columns.duration") },
      { key: "value3p", label: t("visits:columns.value3p") },
      { key: "valueHB", label: t("visits:columns.valueHB") },
      { key: "emp3p", label: t("visits:columns.emp3p") },
      { key: "employeeId", label: t("visits:columns.employeeId") },
      { key: "status3p", label: t("visits:columns.status3p") },
      { key: "statusHB", label: t("visits:columns.statusHB") },
      { key: "comment", label: t("visits:columns.comment") },
      { key: "check", label: t("visits:columns.check") },
      { key: "actions", label: t("visits:columns.actions") },
    ],
    [t]
  );

  const hasNoRows = !isFetching && paginated.length === 0;

  return (
    <div className="manage-page" role="main" aria-label={t("visits:managePage.ariaLabel")}>
      <PendingStockOperationNotice scope="visits" onResolved={(response) => { setSourceStockResult(response.stock_posting || null); setSourceStockError(null); handleRefresh(); }} />
      <StockMovementAuditLoader movementId={auditMovementId} onClose={() => setAuditMovementId(null)} />
      {sourceStockError && <section className="stock-context-feedback" role="alert">{sourceStockError}<button type="button" onClick={() => setSourceStockError(null)}>{t("common:actions.close")}</button></section>}
      {sourceStockResult?.results?.length > 0 && <details className="stock-context-card" open={sourceStockResult.outcome === "partial"}>
        <summary>{t("stock:integration.sourceResultTitle")}</summary>
        <p>{t("stock:integration.materialDateFrozen")}</p>
        {sourceStockResult.results.map(item => <div key={item.material_consumption_id}>
          {item.error && <p role="status">#{item.material_consumption_id} · {item.business_date} · {stockPostingErrorMessage(item.error, t)}</p>}
          <StockPostingContextCard snapshot={item.result} onOpenMovement={setAuditMovementId} />
        </div>)}
        <button type="button" onClick={() => setSourceStockResult(null)}>{t("common:actions.close")}</button>
      </details>}
      <div className="mp-topbar">
        <div className="mp-toolbar">
          <h1>{t("visits:managePage.title")}</h1>

          <div className="mp-toolbar-actions">
            <button
              className="btn-ghost"
              type="button"
              onClick={resetAllWidths}
              title={t("visits:managePage.resetColumnWidthsTitle")}
            >
              {t("visits:managePage.resetWidths")}
            </button>

            <button
              className="btn-secondary"
              type="button"
              onClick={handleRefresh}
              title={lastQuery ? t("visits:managePage.refreshDataTitle") : t("visits:managePage.applyFiltersFirstTitle")}
              disabled={!lastQuery || isFetching}
            >
              {t("common:actions.refresh")}
            </button>

            {!filtersOpen && (
              <button
                className="btn-secondary"
                type="button"
                onClick={toggleFiltersOpen}
                aria-expanded={filtersOpen}
                aria-controls="filters-drawer"
              >
                {t("visits:managePage.showFilters")}
              </button>
            )}
          </div>
        </div>

        {(error || message || visitCheckWarning) && (
          <div className="mp-toast-layer" aria-live="polite" aria-atomic="true">
            {error ? (
              <div className="mp-toast mp-toast--error" role="status">
                {error}
              </div>
            ) : visitCheckWarning ? (
              <VisitCheckWarningToast
                warning={visitCheckWarning.warning}
                summary={visitCheckWarning.summary}
              />
            ) : (
              <div className="mp-toast mp-toast--success" role="status">
                {message}
              </div>
            )}
          </div>
        )}
      </div>

      {filtersOpen && (
        <div className="mp-filters-drawer" id="filters-drawer" aria-label={t("visits:managePage.filtersAriaLabel")}>
          <div className="mp-filters-grid">
            <div className="mp-filters-left">
              <div className="mp-filter-group mp-filter-group-period">
                <div className="mp-period-picker-slot">
                  <EvaluationPeriodPicker period={evaluationPeriod} />
                </div>
              </div>

              <div className="mp-filter-group user-group mp-filter-group-users">
                <label htmlFor="userIds">{t("visits:managePage.usersLabel")}</label>

                {isPrivileged ? (
                  <select
                    id="userIds"
                    multiple
                    className="user-dropdown"
                    value={filters.userIds}
                    onChange={(e) => {
                      const selected = Array.from(
                        e.target.selectedOptions,
                        (opt) => String(opt.value)
                      );

                      setFilters((prev) => ({
                        ...prev,
                        userIds:
                          selected.includes("unassigned") &&
                          prev.userIds.includes("unassigned")
                            ? selected.filter((id) => id !== "unassigned")
                            : selected,
                      }));

                      resetPage();
                    }}
                  >
                    <option value="unassigned">{t("visits:managePage.noUserUnassigned")}</option>

                    {users.map((u) => (
                      <option key={u.id} value={String(u.id)}>
                        {u.first_name} {u.last_name} ({u.username})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="user-fixed"
                    value={`${currentUser.first_name || ""} ${
                      currentUser.last_name || ""
                    } (${currentUser.username || ""})`}
                    readOnly
                  />
                )}
              </div>
            </div>

            <div className="mp-filter-actions">
              <button
                className="btn-primary"
                type="button"
                onClick={handleApplyFilters}
                disabled={isFetching}
              >
                {isFetching ? t("visits:managePage.loading") : t("visits:managePage.applyFilters")}
              </button>

              <button
                className="btn-secondary"
                type="button"
                onClick={toggleFiltersOpen}
                aria-expanded={filtersOpen}
                aria-controls="filters-drawer"
              >
                {t("visits:managePage.hideFilters")}
              </button>

              <button
                className="btn-ghost"
                type="button"
                onClick={resetFilters}
                title={t("visits:managePage.resetFiltersTitle")}
              >
                {t("common:actions.reset")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mp-table-wrap" ref={tableWrapRef}>
        <div
          className={`mp-table-scroller ${isFetching ? "is-loading" : ""}`}
          aria-busy={isFetching}
        >
          {isFetching && (
            <div className="mp-loading-overlay" aria-hidden="true">
              <div className="mp-loading-chip">{t("visits:managePage.loading")}</div>
            </div>
          )}

          <table className="data-table">
            <colgroup>
              {COL_KEYS.map((key) => (
                <col
                  key={key}
                  width={widthAttrFor(key)}
                  style={{ width: widthFor(key) }}
                />
              ))}
            </colgroup>

            <thead>
              <tr>
                {headerCols.map(({ key, label }) => (
                  <th key={key} style={{ width: widthFor(key) }}>
                    <div className="th-inner">
                      <span className="th-label" title={label}>
                        {label}
                      </span>

                      <span
                        className="col-resizer"
                        title={t("visits:managePage.columnResizeTitle")}
                        onMouseDown={(e) => startResize(e, key)}
                        onDoubleClick={() => autoFitColumn(key)}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {hasNoRows && (
                <tr>
                  <td colSpan={COL_KEYS.length}>{t("visits:managePage.noRows")}</td>
                </tr>
              )}

              {paginated.map((row) => {
                const id = String(row.visit_id);
                const pending = isRowPending(id);
                const rowClass =
                  row.status === "Niewykonane" ? "row-niewykonane" : "";

                const d = drafts[id] || {};

                const valueHB =
                  d.service_value !== undefined
                    ? d.service_value
                    : row.service_value ?? "";

                const employeeId =
                  d.employee_id !== undefined
                    ? d.employee_id
                    : row.employee_id ?? "";

                const statusHB =
                  d.status !== undefined ? d.status : row.status ?? "Niewykonane";

                const comment =
                  d.status_comment !== undefined
                    ? d.status_comment
                    : row.status_comment ?? "";

                const checked =
                  d.checked !== undefined ? !!d.checked : !!row.checked;

                return (
                  <tr key={id} className={rowClass}>
                    <td>
                      <div className="cell" style={cellWidthStyle("id")}>
                        {row.visit_id}
                      </div>
                    </td>

                    <td>
                      <div className="cell" style={cellWidthStyle("date")}>
                        {row.date_time}
                      </div>
                    </td>

                    <td>
                      <div className="cell" style={cellWidthStyle("reservation")}>
                        {row.reservation_id}
                      </div>
                    </td>

                    <td>
                      <div className="cell" style={cellWidthStyle("category")}>
                        {row.category}
                      </div>
                    </td>

                    <td>
                      <div className="cell" style={cellWidthStyle("service")}>
                        {row.service}
                      </div>
                    </td>

                    <td>
                      <div className="cell" style={cellWidthStyle("client")}>
                        {row.client}
                      </div>
                    </td>

                    <td>
                      <div className="cell" style={cellWidthStyle("duration")}>
                        {row.duration}
                      </div>
                    </td>

                    <td>
                      <div className="cell" style={cellWidthStyle("value3p")}>
                        {row.service_value3P != null ? row.service_value3P : ""}
                      </div>
                    </td>

                    <td>
                      <div
                        className="cell cell--editor"
                        style={cellWidthStyle("valueHB")}
                      >
                        <input
                          className="cell-input number"
                          type="number"
                          step="0.01"
                          value={valueHB}
                          disabled={pending}
                          onChange={(e) =>
                            setDraft(id, "service_value", e.target.value)
                          }
                          onBlur={() =>
                            commitField(id, "service_value", valueHB)
                          }
                          title={String(valueHB)}
                          inputMode="decimal"
                        />
                      </div>
                    </td>

                    <td>
                      <div className="cell" style={cellWidthStyle("emp3p")}>
                        {row.employee3P}
                      </div>
                    </td>

                    <td>
                      <div
                        className="cell cell--editor"
                        style={cellWidthStyle("employeeId")}
                      >
                        <select
                          value={String(employeeId)}
                          disabled={pending}
                          onChange={(e) => {
                            setDraft(id, "employee_id", e.target.value);
                            commitField(id, "employee_id", e.target.value);
                          }}
                          className="employee-dropdown"
                          title={String(employeeId)}
                        >
                          <option value="">{t("visits:managePage.selectEmployee")}</option>

                          {users.map((u) => (
                            <option key={u.id} value={String(u.id)}>
                              {u.first_name} {u.last_name} ({u.username})
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>

                    <td>
                      <div className="cell" style={cellWidthStyle("status3p")}>
                        {row.status3P}
                      </div>
                    </td>

                    <td>
                      <div
                        className="cell cell--editor"
                        style={cellWidthStyle("statusHB")}
                      >
                        <select
                          value={String(statusHB)}
                          disabled={pending}
                          onChange={(e) => {
                            setDraft(id, "status", e.target.value);
                            commitField(id, "status", e.target.value);
                          }}
                          className="status-dropdown"
                          title={String(statusHB)}
                        >
                          <option value="Niewykonane">{t("visits:status.notPerformed")}</option>
                          <option value="Wykonane">{t("visits:status.performed")}</option>
                        </select>
                      </div>
                    </td>

                    <td>
                      <div
                        className="cell cell--editor"
                        style={cellWidthStyle("comment")}
                      >
                        <textarea
                          value={comment}
                          disabled={pending}
                          onChange={(e) =>
                            setDraft(id, "status_comment", e.target.value)
                          }
                          onBlur={() =>
                            commitField(id, "status_comment", comment)
                          }
                          className="comment-box"
                          title={String(comment || "")}
                        />
                      </div>
                    </td>

                    <td>
                      <div
                        className="cell cell--editor"
                        style={cellWidthStyle("check")}
                      >
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={checked}
                          disabled={pending}
                          onChange={(e) => {
                            setDraft(id, "checked", e.target.checked);
                            commitField(id, "checked", e.target.checked);
                          }}
                          title={String(checked)}
                        />
                      </div>
                    </td>

                    <td>
                      <div
                        className="cell cell--editor"
                        style={cellWidthStyle("actions")}
                      >
                        <button
                          className="delete-button"
                          type="button"
                          disabled={pending}
                          onClick={() => handleDeleteRow(id)}
                          title={t("visits:managePage.deleteVisitTitle")}
                        >
                          {t("visits:managePage.deleteVisit")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <PaginationFooter
          className="mp-pagination"
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50]}
          totalRows={totalRows}
          disabled={isFetching}
          onPageSizeChange={handlePageSizeChange}
          onPrev={goPrev}
          onNext={goNext}
        />
      </div>
    </div>
  );
};

export default ManagePage;



